import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { portalTranslations } from '../i18n/portalTranslations';
import {
  deleteShipmentDocument,
  getDocumentDownloadUrl,
  listShipmentDocuments,
  uploadShipmentDocument,
} from '../services/documentService';
import { formatPortalDate } from '../services/shipmentService';
import { ShipmentDocument } from '../types/shipment';

interface DocumentPanelProps {
  shipmentId: string;
  canUpload?: boolean;
}

const DocumentPanel: React.FC<DocumentPanelProps> = ({ shipmentId, canUpload = false }) => {
  const { language } = useLanguage();
  const pt = portalTranslations[language].portal;
  const at = portalTranslations[language].admin;
  const [docs, setDocs] = useState<ShipmentDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const reload = async () => {
    setLoading(true);
    const rows = await listShipmentDocuments(shipmentId);
    setDocs(rows);
    setLoading(false);
  };

  useEffect(() => {
    reload();
  }, [shipmentId]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    const created = await uploadShipmentDocument(shipmentId, file);
    setUploading(false);
    e.target.value = '';
    if (!created) {
      setError(language === 'ar' ? 'تعذّر رفع الملف. تأكد من إعداد Storage.' : 'Upload failed. Check Storage setup.');
      return;
    }
    await reload();
  };

  const handleDownload = async (doc: ShipmentDocument) => {
    const url = await getDocumentDownloadUrl(doc.filePath);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDelete = async (doc: ShipmentDocument) => {
    await deleteShipmentDocument(doc);
    await reload();
  };

  return (
    <section className="document-panel">
      <div className="document-panel-head">
        <h2>{pt.documents}</h2>
        {canUpload && (
          <label className="btn btn-secondary document-upload-btn">
            {uploading ? (language === 'ar' ? 'جاري الرفع...' : 'Uploading...') : at.uploadDoc}
            <input type="file" hidden disabled={uploading} onChange={handleUpload} />
          </label>
        )}
      </div>
      {error && <p className="portal-error">{error}</p>}
      {loading ? (
        <p className="portal-empty">{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
      ) : docs.length === 0 ? (
        <p className="portal-empty">{pt.noDocuments}</p>
      ) : (
        <ul className="document-list">
          {docs.map((doc) => (
            <li key={doc.id} className="document-item">
              <div>
                <strong>{doc.fileName}</strong>
                <span className="portal-updated">{formatPortalDate(doc.uploadedAt, language)}</span>
              </div>
              <div className="document-actions">
                <button type="button" className="btn btn-secondary" onClick={() => handleDownload(doc)}>
                  {pt.download}
                </button>
                {canUpload && (
                  <button type="button" className="btn btn-secondary" onClick={() => handleDelete(doc)}>
                    {at.deleteDoc}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default DocumentPanel;
